/**
 * PortPool — allocates ports from a [min, max] range, checks OS for collisions.
 */

import { createConnection } from "node:net";

/**
 * Allocates TCP ports from a `[min, max]` range with OS-level collision detection.
 * Used by service connector adapters (devserver, static-server) to assign local ports.
 * @docLink packages/connectors/api-reference#port-pool
 */
export class PortPool {
  private readonly min: number;
  private readonly max: number;
  private readonly inUse = new Set<number>();

  constructor(range: [number, number]) {
    this.min = range[0];
    this.max = range[1];
  }

  /** Allocate the next free port from the range. Checks OS for collisions. */
  async allocate(): Promise<number> {
    for (let port = this.min; port <= this.max; port++) {
      if (this.inUse.has(port)) continue;
      if (!(await PortPool.isPortFree(port))) {
        this.inUse.add(port);
        continue;
      }
      this.inUse.add(port);
      return port;
    }
    throw new Error(`No free port in range ${this.min}-${this.max}`);
  }

  /** Mark a port as in-use (for explicit port args or registered instances). */
  reserve(port: number): void {
    this.inUse.add(port);
  }

  /** Return a port to the pool. */
  release(port: number): void {
    this.inUse.delete(port);
  }

  /** Check if a port is currently tracked as in-use. */
  isInUse(port: number): boolean {
    return this.inUse.has(port);
  }

  /** Quick TCP connect test — returns true if nothing is listening. */
  static isPortFree(port: number, host = "127.0.0.1"): Promise<boolean> {
    return new Promise((resolve) => {
      const socket = createConnection({ port, host });
      socket.once("connect", () => {
        socket.destroy();
        resolve(false);
      });
      socket.once("error", () => {
        socket.destroy();
        resolve(true);
      });
      socket.setTimeout(500, () => {
        socket.destroy();
        resolve(true);
      });
    });
  }
}
